/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from 'react';
import Select from '../../../../../components/form/elements/select';
import RadioGroup from '../../../../../components/form/elements/radio-group';
import TextArea from '../../../../../components/form/elements/text-area';
import { map } from 'rxjs/operators';
import { useParams } from 'react-router-dom';
import { useService } from '../../../../../services/services';
import { useTranslation } from 'react-i18next';
import Decimal from '../../../../../components/form/elements/decimal';
import Watch from '../../../../../components/form/elements/watch';
import StatusEnum from '../enums/event-status';
import { FormComponent } from '../../../../../components/form';
import File from '../../../../../components/form/elements/file';
import Field from '../../../../../interfaces/field';
import Disabled from '../../../../../components/form/elements/disabled';
import PeriodDates from './period-dates';
import Text from '../../../../../components/form/elements/text';

function useFormBody({
  days,
  setDays,
}: {
  days: number;
  setDays: (value: number) => void;
}) {
  const { animalEventService, fieldService } = useService();

  const { farmId } = useParams<{
    farmId: string;
  }>();

  const { t } = useTranslation();

  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback(
    (key: string, params?: { days: number }) =>
      t(transKeyPrefix.concat(key), params),
    [t]
  );

  return useMemo<FormComponent[]>(
    () => [
      RadioGroup(
        'status',
        [
          {
            label: tp('events.grazing.planned'),
            value: String(StatusEnum.planned),
          },
          {
            label: tp('events.grazing.completed'),
            value: String(StatusEnum.completed),
          },
        ],
        null,
        true
      ),
      (rest) => <PeriodDates days={days} setDays={setDays} rest={rest} />,
      Decimal(
        'duration_per_day',
        tp('events.grazing.duration_per_day'),
        null,
        false,
        true,
        true
      ),
      Watch(
        Select('field_no', {
          options: [],
          asyncOptions: (value, _loadedOptions, { page }) => {
            const season = new Date().getFullYear();
            return fieldService
              .index(
                farmId,
                season.toString(),
                { page, lad_number_search: value },
                true
              )
              .pipe(
                map((response: any) => ({
                  hasMore: false,
                  options: response.data.map((field: Field) => ({
                    ...field,
                    label: field.lad_number,
                    value: field.id,
                  })),
                  additional: { page },
                }))
              );
          },
          label: tp('events.grazing.field_no'),
          isClearable: true,
          column: true,
        }),
        ({ setValue, value }) => {
          setValue('bloka_nr', value.block_number);
          setValue('field_area', value.area);
          setValue('field_name', {
            ...value,
            value: value.id,
            label: value.name,
          });
        }
      ),
      Watch(
        Select('field_name', {
          options: [],
          asyncOptions: (value, _loadedOptions, { page, getValues }) => {
            const season = new Date().getFullYear();
            return fieldService
              .index(farmId, season.toString(), { page, search: value }, true)
              .pipe(
                map((response: any) => ({
                  hasMore: false,
                  options: response.data.map((field: Field) => ({
                    ...field,
                    value: field.id,
                    label: field.name,
                  })),
                  additional: { page },
                }))
              );
          },
          label: tp('events.grazing.field_name'),
          column: true,
          isClearable: true,
        }),
        ({ setValue, value }) => {
          setValue('bloka_nr', value.block_number);
          setValue('field_area', value.area);
          setValue('field_no', {
            ...value,
            value: value.id,
            label: value.lad_number,
          });
        }
      ),
      Disabled(
        Text('bloka_nr', tp('events.grazing.block_no'), null, { column: true })
      ),
      Disabled(
        Decimal(
          'field_area',
          tp('events.grazing.field_area_ha'),
          null,
          false,
          true,
          true
        )
      ),
      Decimal(
        'grazing_area_ha',
        tp('events.grazing.grazing_area_ha'),
        null,
        false,
        true,
        true
      ),
      TextArea('notes', tp('events.notes'), undefined, false, undefined, true),
      File(
        'file',
        null,
        false,
        animalEventService,
        true,
        transKeyPrefix.concat('animals.')
      ),
    ],
    [days]
  );
}

export default useFormBody;
